import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useExpertContext } from '../context/ExpertContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiMapPin, FiBook, FiMail, FiUser, FiSend } = FiIcons;

const ExpertProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { experts } = useExpertContext();
  const [expert, setExpert] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    requester_name: '',
    requester_email: '',
    message: ''
  });

  useEffect(() => {
    const foundExpert = experts.find(e => e.id === id && e.status === 'approved');
    if (foundExpert) {
      setExpert(foundExpert);
    } else {
      navigate('/');
    }
  }, [id, experts, navigate]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    // Simulate sending email
    alert('Message sent successfully!');
    setShowContactForm(false);
    setContactForm({ requester_name: '', requester_email: '', message: '' });
  };

  if (!expert) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="text-6xl text-gray-300 mb-4">404</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Expert Not Found</h2>
          <p className="text-gray-600 mb-6">
            The expert you're looking for doesn't exist or is not approved yet.
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary text-white px-6 py-3 rounded-lg"
          >
            Back to Map
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-4xl mx-auto"
      >
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 text-gray-600 hover:text-green-600 mb-6 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span>Back to Map</span>
        </button>

        {/* Expert Header */}
        <div className="expert-card mb-6">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiUser} className="text-white text-3xl" />
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{expert.full_name}</h1>
              <div className="flex items-center space-x-2 text-gray-600 mb-3">
                <SafeIcon icon={FiMapPin} className="text-green-500" />
                <span className="text-lg">{expert.city}, {expert.governorate}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <SafeIcon icon={FiMail} className="text-blue-500" />
                <span>{expert.email}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            {expert.bio && (
              <div className="expert-card">
                <h2 className="text-xl font-bold text-gray-800 mb-4">About</h2>
                <p className="text-gray-600 leading-relaxed">{expert.bio}</p>
              </div>
            )}

            {/* Support Topics */}
            {expert.support_topics && expert.support_topics.length > 0 && (
              <div className="expert-card">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <SafeIcon icon={FiBook} className="text-blue-600" />
                  <span>Areas of Expertise</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {expert.support_topics.map((topic, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-blue-800 font-medium">{topic}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Form */}
            {showContactForm && (
              <div className="expert-card">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Send Message</h2>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={contactForm.requester_name}
                        onChange={(e) => setContactForm({...contactForm, requester_name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={contactForm.requester_email}
                        onChange={(e) => setContactForm({...contactForm, requester_email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      placeholder="Describe how you'd like the expert to help you with Minecraft Education..."
                    />
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowContactForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-primary text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiSend} />
                      <span>Send Message</span>
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="expert-card">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Get in Touch</h3>
              
              {!showContactForm ? (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="w-full btn-primary text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSend} />
                    <span>Send Message</span>
                  </button>
                  
                  <a
                    href={`mailto:${expert.email}`}
                    className="w-full btn-secondary text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiMail} />
                    <span>Direct Email</span>
                  </a>
                  
                  <p className="text-sm text-gray-600 text-center">
                    Choose your preferred way to contact {expert.full_name.split(' ')[0]}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Fill out the form to send a message to {expert.full_name.split(' ')[0]}. 
                  They will receive your message and can respond directly to your email.
                </p>
              )}
            </div>

            {/* Location Card */}
            <div className="expert-card">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Location</h3>
              <div className="flex items-center space-x-3 text-gray-600">
                <SafeIcon icon={FiMapPin} className="text-green-500 text-xl" />
                <div>
                  <div className="font-medium text-gray-800">{expert.governorate}</div>
                  {expert.city && <div className="text-sm">{expert.city}</div>}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="expert-card">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Member since:</span>
                  <span className="font-medium">
                    {new Date(expert.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Support areas:</span>
                  <span className="font-medium">
                    {expert.support_topics?.length || 0} topics
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{expert.governorate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ExpertProfile;